import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Download, Sparkles, AlertCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// NOTE: This is Google's test key that only works on localhost
// Get your own keys from: https://www.google.com/recaptcha/admin/create
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

export default function GenerationForm() {
  const { usage, fetchUsage, user } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [description, setDescription] = useState('');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [testScenarios, setTestScenarios] = useState('');
  const [promptInstructions, setPromptInstructions] = useState('');
  const [platform, setPlatform] = useState('Azure DevOps');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);

  const isLimitReached = usage.tier !== 'paid' && usage.used >= usage.limit;
  // reCAPTCHA disabled for now - removed recaptchaToken requirement
  const canGenerate = description.trim() && platform && !isLimitReached;

  const handleGenerate = async () => {
    if (!canGenerate) {
      if (isLimitReached) {
        toast.error('Daily limit reached. Please upgrade for unlimited access.');
        return;
      }
      // reCAPTCHA check disabled for now
      // if (!recaptchaToken) {
      //   toast.error('Please complete the reCAPTCHA verification');
      //   return;
      // }
      return;
    }

    if (description.length > 6000) {
      toast.error('Description must be under 6000 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/generate`,
        {
          description,
          acceptance_criteria: acceptanceCriteria || null,
          test_scenarios: testScenarios || null,
          prompt_instructions: promptInstructions || null,
          platform,
          recaptchaToken
        },
        {
          responseType: 'blob',
          withCredentials: true,
          timeout: 60000 // 60 second timeout for LLM generation
        }
      );

      // Check if response is an error (sometimes errors come as blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Generation failed');
      }

      // Download CSV file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `TestCases_${platform.replace(' ', '_')}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Test cases generated successfully!');
      
      // Reset form
      setDescription('');
      setAcceptanceCriteria('');
      setTestScenarios('');
      setPromptInstructions('');
      setRecaptchaToken('');
      
      // Reset reCAPTCHA using ref
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
        } catch (err) {
          console.warn('reCAPTCHA reset failed:', err);
        }
      }

      // Refresh usage
      await fetchUsage();
    } catch (error) {
      console.error('Generation error:', error);
      
      let errorMsg = 'Generation failed. Please try again.';
      
      if (error.response?.data) {
        if (error.response.data.type === 'application/json') {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMsg = errorData.detail || errorMsg;
          } catch (e) {
            // If parsing fails, use default message
          }
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      // Special handling for reCAPTCHA errors
      if (errorMsg.includes('reCAPTCHA')) {
        errorMsg = 'reCAPTCHA verification failed. Please try again. If the issue persists, the site may need production reCAPTCHA keys configured.';
      }
      
      toast.error(errorMsg);
      
      if (error.response?.status === 429) {
        await fetchUsage();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="max-w-3xl mx-auto"
    >
      <div className="bg-card border border-border rounded-lg p-8 shadow-xl" data-testid="generation-form">
        {isLimitReached && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="limit-warning">
            <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-500">Daily Limit Reached</p>
              <p className="text-sm text-muted-foreground mt-1">
                {usage.tier === 'anonymous' ? (
                  <>
                    You've used all 5 free generations for today. Try again tomorrow.
                    {/* Sign up prompt - Hidden for now */}
                    {/* , or{' '}
                    <button
                      onClick={() => navigate('/signup')}
                      className="text-blue-500 hover:underline font-medium"
                      data-testid="signup-prompt-link"
                    >
                      sign up for a free account
                    </button>
                    {' '}to get 10 free generations per day. */}
                  </>
                ) : (
                  <>
                    You've used all {usage.limit} generations today. Please try again tomorrow.
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Description */}
          <div>
            <Label htmlFor="description" className="flex items-center justify-between">
              <span>Description *</span>
              <span className="text-xs text-muted-foreground">{description.length}/6000</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 bg-black border-input min-h-[120px]"
              placeholder="Describe the feature or functionality to test..."
              maxLength={6000}
              required
              disabled={isLimitReached}
              data-testid="description-input"
            />
          </div>

          {/* Acceptance Criteria */}
          <div>
            <Label htmlFor="acceptance" className="flex items-center justify-between">
              <span>Acceptance Criteria (Optional)</span>
              <span className="text-xs text-muted-foreground">{acceptanceCriteria.length}/3000</span>
            </Label>
            <Textarea
              id="acceptance"
              value={acceptanceCriteria}
              onChange={(e) => setAcceptanceCriteria(e.target.value)}
              className="mt-1 bg-black border-input min-h-[100px]"
              placeholder="List the acceptance criteria..."
              maxLength={3000}
              disabled={isLimitReached}
              data-testid="acceptance-criteria-input"
            />
          </div>

          {/* Test Scenarios */}
          <div>
            <Label htmlFor="scenarios" className="flex items-center justify-between">
              <span>High-level Test Scenarios (Optional)</span>
              <span className="text-xs text-muted-foreground">{testScenarios.length}/3000</span>
            </Label>
            <Textarea
              id="scenarios"
              value={testScenarios}
              onChange={(e) => setTestScenarios(e.target.value)}
              className="mt-1 bg-black border-input min-h-[100px]"
              placeholder="Outline the high-level test scenarios..."
              maxLength={3000}
              disabled={isLimitReached}
              data-testid="test-scenarios-input"
            />
          </div>

          {/* Prompt Instructions */}
          <div>
            <Label htmlFor="instructions" className="flex items-center justify-between">
              <span>Prompt Instructions (Optional)</span>
              <span className="text-xs text-muted-foreground">{promptInstructions.length}/3000</span>
            </Label>
            <Textarea
              id="instructions"
              value={promptInstructions}
              onChange={(e) => setPromptInstructions(e.target.value)}
              className="mt-1 bg-black border-input min-h-[80px]"
              placeholder="Any specific instructions for test case generation..."
              maxLength={3000}
              disabled={isLimitReached}
              data-testid="prompt-instructions-input"
            />
          </div>

          {/* Platform Selection */}
          <div className="mb-12">
            <Label htmlFor="platform">Platform *</Label>
            <Select value={platform} onValueChange={setPlatform} disabled={isLimitReached}>
              <SelectTrigger className="mt-1 bg-black border-input" data-testid="platform-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Azure DevOps">Azure DevOps</SelectItem>
                <SelectItem value="JIRA">JIRA</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Generate Button - Moved directly below Platform */}
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate || loading}
            className="w-full mt-8 bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90 font-medium shadow-[0_0_30px_rgba(59,130,246,0.4)]"
            size="lg"
            data-testid="generate-button"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Generate and Download
              </>
            )}
          </Button>

          {/* Share Feedback Button - reCAPTCHA disabled for now */}
          <div className="flex items-start gap-4">
            {/* reCAPTCHA - Disabled for now, will be enabled later */}
            {/* <div className="flex-1">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setRecaptchaToken(token)}
                onErrored={() => {
                  console.error('reCAPTCHA error occurred');
                  toast.error('reCAPTCHA failed to load. Please refresh the page.');
                }}
                onExpired={() => {
                  setRecaptchaToken('');
                  toast.warning('reCAPTCHA expired. Please verify again.');
                }}
                theme="dark"
                data-testid="recaptcha"
              />
              {RECAPTCHA_SITE_KEY === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' && (
                <p className="text-xs text-yellow-500 mt-2">
                  ⚠️ Using test reCAPTCHA key. This only works on localhost. 
                  <a 
                    href="https://www.google.com/recaptcha/admin/create" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline ml-1"
                  >
                    Get production keys
                  </a>
                </p>
              )}
            </div> */}
            <Button
              type="button"
              onClick={() => navigate('/feedback')}
              className="h-[78px] px-6 self-start ml-auto bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-semibold"
              data-testid="share-feedback-button"
            >
              <svg 
                className="h-5 w-5 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" 
                />
              </svg>
              Share Feedback
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
